import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { UtilFormTypes } from "models/formTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { SearchInputProps, TextInputProps, WintFormConnect } from "models/propTypes/wintFormPropsTypes";
import { COST_FORWARDING_DESCRIPTION_MAX_LENGTH, hasCostForwardingCustomer } from "./costForwardingMarkingModel";
import { createCostForwardingCustomerSources } from "./costForwardingCustomerSources";
//#endregion
//#region components
import { SearchInput, TextInput, VerticalFormField } from "components/common/wintForms";
import { useCustomerSearch } from "components/customer";
import { useCustomerService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

interface CostForwardingCustomerFieldsProps {
  connect: WintFormConnect<UtilFormTypes.CostForwardingMarking>;
  /**
   * "form" in the to-do and create forms, where the customer is optional and nothing above it says what it
   * is for; "dialog" on the document page, whose title and help text already do.
   */
  presentation: "form" | "dialog";
  disabled?: boolean;
}

/** Customer and description of a marking, shared by the to-do and create forms and the document page's dialog. */
const CostForwardingCustomerFields: React.FC<CostForwardingCustomerFieldsProps> = ({
  connect,
  presentation,
  disabled,
}) => {
  const customerService = useCustomerService();
  const searchCustomers = useCustomerSearch();
  const [topCustomers, setTopCustomers] = React.useState<AutocompleteListItem[]>([]);

  const customerSources = createCostForwardingCustomerSources(
    { getCustomerSuggestions: customerService.getCustomerSuggestions, searchCustomers },
    {
      icon: IconDefinitionConstants.WCompany,
      group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
    }
  );

  // Fetched here rather than with the form, so that a document that is never forwarded costs no request.
  React.useEffect(() => {
    if (disabled) return;

    customerSources.loadSuggestions().then(setTopCustomers);
  }, [disabled]);

  const isForm = presentation === "form";
  const customerConnect = connect("costForwardingCustomer");
  // In the forms the description only means something once there is a customer to invoice it to.
  const showDescription = !isForm || hasCostForwardingCustomer(customerConnect.value);

  return (
    <>
      <VerticalFormField<SearchInputProps>
        Component={SearchInput}
        {...customerConnect}
        label={isForm ? TranslationKeys.CostForwarding.Customer : TranslationKeys.Common.Customer}
        descriptionText={isForm ? TranslationKeys.CostForwarding.HelpText : undefined}
        items={customerSources.search}
        suggestedItems={topCustomers}
        optional={isForm}
        disabled={disabled}
      />
      {showDescription && (
        <VerticalFormField<TextInputProps>
          Component={TextInput}
          {...connect("costForwardingDescription")}
          label={TranslationKeys.CostForwarding.Description}
          maxLength={COST_FORWARDING_DESCRIPTION_MAX_LENGTH}
          optional
          disabled={disabled}
        />
      )}
    </>
  );
};

export default CostForwardingCustomerFields;
